import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminPostingRow } from '@/services/adminApi';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';

/**
 * AdminPostingsPage — cross-tenant view of `jobPostings`.
 *
 * Filters: status (active/inactive), channel, companyId (URL+manual),
 * created date range, plus inline title search across loaded rows.
 */
export default function AdminPostingsPage() {
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');
  const [channel, setChannel] = useState('all');
  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [titleSearch, setTitleSearch] = useState('');
  const [searchInline, setSearchInline] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => { setSortBy(field); setSortDir(dir); };

  const filterParams = useMemo(() => ({
    ...(activeFilter !== 'all' ? { isActive: activeFilter === 'true' } : {}),
    ...(channel !== 'all' ? { channel } : {}),
    ...(companyId ? { companyId } : {}),
    ...(createdFrom ? { createdFrom } : {}),
    ...(createdTo ? { createdTo } : {}),
    ...(titleSearch ? { titleSearch } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [activeFilter, channel, companyId, createdFrom, createdTo, titleSearch, sortBy, sortDir]);

  const { exportCsv, isExporting } = useAdminExportCsv('postings', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.postings.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminPostingRow>(fetchPage, [filterParams]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      (r.title || '').toLowerCase().includes(s)
      || (r.companyName ?? '').toLowerCase().includes(s)
      || (r.positionTitle ?? '').toLowerCase().includes(s)
      || (r.companyId ?? '').toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (activeFilter !== 'all') activeFilters.push({ key: 'active', label: activeFilter === 'true' ? 'Active' : 'Inactive', onClear: () => setActiveFilter('all') });
  if (channel !== 'all') activeFilters.push({ key: 'channel', label: `Channel: ${channel}`, onClear: () => setChannel('all') });
  if (companyId) activeFilters.push({ key: 'company', label: `Company: ${companyId.slice(0, 10)}…`, onClear: () => setCompanyId('') });
  if (createdFrom) activeFilters.push({ key: 'from', label: `From: ${createdFrom}`, onClear: () => setCreatedFrom('') });
  if (createdTo) activeFilters.push({ key: 'to', label: `To: ${createdTo}`, onClear: () => setCreatedTo('') });
  if (titleSearch) activeFilters.push({ key: 'title-srv', label: `Title: "${titleSearch}"`, onClear: () => setTitleSearch('') });
  if (searchInline) activeFilters.push({ key: 'search', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setActiveFilter('all'); setChannel('all'); setCompanyId('');
    setCreatedFrom(''); setCreatedTo(''); setTitleSearch(''); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Job postings"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search loaded rows (title, company, position)..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[280px]"
        />
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="h-9 w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any channel</SelectItem>
            <SelectItem value="company_website">Website</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Company ID..."
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="h-9 w-[220px] font-mono text-xs"
        />
        <Input
          placeholder="Title contains..."
          value={titleSearch}
          onChange={(e) => setTitleSearch(e.target.value)}
          className="h-9 w-[180px]"
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Created</span>
          <Input type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} className="h-9 w-[150px]" />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} className="h-9 w-[150px]" />
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4">
                    <SortableTableHeader label="Posting" field="title" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Company</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Channel" field="channel" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Status</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Created" field="createdAt" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">Loading...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">No postings.</td></tr>
                )}
                {filtered.map((row) => {
                  const detailHref = row.companyId && row.jobPositionId
                    ? `/admin/customers/${row.companyId}/positions/${row.jobPositionId}/postings/${row.postingId}`
                    : null;
                  const displayTitle = row.title
                    || (row.positionTitle ? `${row.positionTitle} · ${row.channel || 'posting'}` : '')
                    || '(untitled)';
                  return (
                    <tr key={row.postingId} className="border-b border-border/30 last:border-0 hover:bg-accent/40">
                      <td className="py-3 px-4">
                        {detailHref ? (
                          <Link to={detailHref} className="font-medium hover:underline">{displayTitle}</Link>
                        ) : (
                          <span className="font-medium">{displayTitle}</span>
                        )}
                        {row.positionTitle && row.title && (
                          <p className="text-xs text-muted-foreground truncate max-w-[280px]">
                            Position: {row.positionTitle}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {row.companyId ? (
                          <Link
                            to={`/admin/customers/${row.companyId}`}
                            className="hover:underline text-foreground"
                            title={row.companyId}
                          >
                            {row.companyName || row.companyId.slice(0, 10) + '…'}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{row.channel || '—'}</td>
                      <td className="py-3 px-3">
                        {row.isActive ? (
                          <span className="text-xs text-success">active</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">inactive</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground text-xs">
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="annuncio"
            entityLabelPlural="annunci"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}
    </div>
  );
}
